// src/pages/About.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Brain, Eye, Heart } from 'lucide-react';
import logo from '../assets/images/WriteApp.png';
import pareshImage from '../assets/images/banner green.png';

const About = () => {
  const navigate = useNavigate();

  const handleBackToHome = () => {
    navigate('/forum');
  };

  return (
    <div className="min-h-screen bg-[#0B0C0D]">
      {/* Navigation Bar - Updated to match common navbar */}
      <nav className="fixed top-0 w-full bg-black/30 backdrop-blur-lg border-b border-white/10 z-50 h-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-full">
            <Link to="/forum" className="flex items-center gap-2">
              <img src={logo} alt="WriteApp Logo" className="h-12 w-auto" />
            </Link>
            <motion.button
              onClick={handleBackToHome}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Back to Home
            </motion.button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                About
              </span>
              <span className="text-white"> Our Platform</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              At WriteApp.in, we believe that your handwriting is more than just a way to communicate, it's a deeply personal expression of your subconscious mind, your emotional wiring, and your unique personality
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content Section - Better Layout */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gray-900/50 backdrop-blur-sm rounded-3xl border border-gray-800 overflow-hidden"
          >
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Image Side */}
              <div className="relative p-8 lg:p-12 flex items-center justify-center bg-gradient-to-br from-blue-500/5 to-purple-500/5">
                <motion.img
                  src={pareshImage}
                  alt="Paresh Chitnis"
                  className="w-full max-w-md rounded-2xl shadow-2xl"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                />
              </div>

              {/* Content Side */}
              <div className="p-8 lg:p-12 space-y-6">
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-8">
                  The Science Behind <span className="text-blue-400">Your Handwriting</span>
                </h2>

                <div className="space-y-6 text-gray-400 text-lg leading-relaxed">
                  <p>
                    Every curve, pressure, space, and slant in your handwriting carries subtle yet powerful clues about your thoughts, behaviors, fears, strengths, and decision-making patterns. That's why we've built a platform where handwriting analysis is not only free and accessible, but also deeply insightful, accurate, and backed by experienced graphologists who understand the psychology behind your strokes.
                  </p>

                  <div className="bg-blue-500/10 rounded-2xl p-6 border border-blue-500/20">
                    <h3 className="text-xl font-semibold text-white mb-3">Real Human Expertise</h3>
                    <p>
                      WriteApp.in is not just another personality quiz or automated result generator. Here, real human experts decode your handwriting sample using advanced principles of graphology and behavioral psychology.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              What Sets Us <span className="text-purple-400">Apart</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800 hover:border-blue-500/30 transition-all duration-300"
            >
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6">
                <Brain className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Deep Analysis</h3>
              <p className="text-gray-400 leading-relaxed">
                When you upload a handwritten sample, our expert graphologists analyze everything from letter size, pressure, slant, spacing, baseline, loops, and zones to understand your personality traits.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800 hover:border-purple-500/30 transition-all duration-300"
            >
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Transparent Process</h3>
              <p className="text-gray-400 leading-relaxed">
                We don't just tell you what kind of person you are, we show you how we know. Every analysis explains the exact handwriting formations that led to our interpretations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800 hover:border-pink-500/30 transition-all duration-300"
            >
              <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center mb-6">
                <Heart className="w-6 h-6 text-pink-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Personal Growth</h3>
              <p className="text-gray-400 leading-relaxed">
                Whether you're dealing with self-doubt, seeking emotional clarity, or curious about hidden fears, your handwriting reveals more than you can imagine.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Detailed Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 ">
        <div className="max-w-4xl mx-auto max-w-screen-2xl">
          <div className="flex flex-col lg:flex-row space-y-12 lg:space-y-0 lg:space-x-12  mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gray-900/30 backdrop-blur-sm rounded-3xl p-8 lg:p-12 border border-gray-800 flex-1"
            >
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-6">Our Mission</h3>
              <p className="text-lg text-gray-400 leading-relaxed mb-6">
                Our mission is to make personality understanding more human, accurate, and accessible. Whether you're exploring your strengths, identifying behavioral patterns, working on self-development, or simply curious about how your mind works, handwriting analysis offers a unique psychological mirror.
              </p>
              <p className="text-lg text-gray-400 leading-relaxed">
                This tool has been used in personal growth journeys, coaching, career guidance, emotional intelligence training, and even forensic profiling. Now, with WriteApp.in, you can access the same depth of insight without paying a fee or waiting endlessly for generic results.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gray-900/30 backdrop-blur-sm rounded-3xl p-8 lg:p-12 border border-gray-800 flex-1"
            >
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-6">What You'll Discover</h3>
              <p className="text-lg text-gray-400 leading-relaxed mb-6">
                By turning your handwriting into a window to your inner world, WriteApp.in helps you explore traits like perfectionism, impulsivity, empathy, attention-seeking, emotional suppression, analytical thinking, sensitivity, and many more.
              </p>
              <p className="text-lg text-gray-400 leading-relaxed">
                Our platform is designed for curious individuals who are ready to dive into the world of self-awareness, emotional patterns, and personality decoding. It's a safe, confidential, and deeply respectful experience where even your most hidden traits are revealed with kindness and clarity.
              </p>
            </motion.div>
          </div>


        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-3xl p-8 lg:p-12 border border-blue-500/20"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Discover Yourself?
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed mb-8 italic">
              If you've ever asked yourself, "Why do I behave this way?", "What's holding me back?", or "Who am I beneath the surface?", then your handwriting might just have the answers. At WriteApp.in, we help you uncover those answers with science, empathy, and expertise.
            </p>
            <p className="text-xl text-blue-400 font-medium">
              Upload your handwriting today and begin a powerful journey of self-discovery.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;